import { Fragment, createContext, useCallback, useContext, useState } from "react";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { useModalStackLayer } from "@/lib/modalStack";

const DialogRequestCloseContext = createContext<(() => void) | null>(null);

export function hasFormChanges<T>(current: T, baseline: T): boolean {
  return JSON.stringify(current) !== JSON.stringify(baseline);
}

export function useDialogRequestClose(fallback?: () => void): () => void {
  const requestClose = useContext(DialogRequestCloseContext);
  return requestClose ?? fallback ?? (() => {});
}

export function useDialogCloseGuard(onClose: () => void, dirty: boolean) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmOpen(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  const confirmDiscard = useCallback(() => {
    setConfirmOpen(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  return {
    requestClose,
    confirmOpen,
    confirmDiscard,
    cancelDiscard,
    DialogRequestCloseContext,
  };
}

/**
 * Abbrechen-Button für Dialog-Footer: läuft über den Close-Guard des Dialogs,
 * damit bei ungespeicherten Änderungen die Bestätigung erscheint.
 */
export function DialogCancelButton({
  onClose,
  disabled,
  label = "Abbrechen",
  variant = "secondary",
}: {
  onClose: () => void;
  disabled?: boolean;
  label?: string;
  variant?: "secondary" | "ghost";
}) {
  const requestClose = useDialogRequestClose(onClose);
  return (
    <Button type="button" variant={variant} onClick={requestClose} disabled={disabled}>
      {label}
    </Button>
  );
}

export function DialogCloseButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Schließen"
      className={clsx(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-label-secondary transition hover:bg-fill-tertiary hover:text-label",
        className,
      )}
    >
      <XMarkIcon className="h-5 w-5" aria-hidden />
    </button>
  );
}

export function DiscardChangesDialog({
  open,
  onConfirm,
  onCancel,
  zIndexClass = "z-[300]",
  title = "Ungespeicherte Änderungen",
  description = "Beim Schließen gehen nicht gespeicherte Eingaben verloren.",
  body = "Möchten Sie wirklich schließen, ohne zu speichern?",
  confirmLabel = "Verwerfen",
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  zIndexClass?: string;
  title?: string;
  description?: string;
  body?: string;
  confirmLabel?: string;
}) {
  const { zIndex } = useModalStackLayer(open);
  return (
    <Transition show={open} as={Fragment}>
      <HuiDialog
        as="div"
        className={clsx("relative", zIndexClass)}
        style={zIndex != null ? { zIndex } : undefined}
        onClose={onCancel}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.99]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.99]"
            >
              <DialogPanel className="w-full max-w-sm transform overflow-hidden rounded-ios-lg bg-card shadow-ios-md transition">
                <div className="border-b border-separator px-5 py-4">
                  <DialogTitle className="text-base font-semibold text-label">
                    {title}
                  </DialogTitle>
                  <p className="mt-1 text-sm text-label-secondary">
                    {description}
                  </p>
                </div>
                <div className="px-5 py-4">
                  <p className="text-sm text-label-secondary">
                    {body}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-separator bg-card-elevated px-5 py-3">
                  <Button type="button" variant="secondary" onClick={onCancel}>
                    Abbrechen
                  </Button>
                  <Button type="button" variant="danger" onClick={onConfirm}>
                    {confirmLabel}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HuiDialog>
    </Transition>
  );
}
